import type { PillTone } from "@/components/brand/Pill";
import type { SectionEnvelope, SectionState, SectionTruncation } from "@/api/diagnostic";

/**
 * Estado de render de uma seção — função **pura**, sem DOM e sem i18n.
 *
 * É aqui que mora a regra dura da Feature: contador cujo número é **desconhecido**
 * (`not_collected`, `error`, `unauthorized`) renderiza `—`, nunca `0`. Zero é uma
 * afirmação sobre o sistema de origem; só `empty` pode fazê-la.
 */

/** O traço que substitui um número desconhecido. Nunca `0`, nunca vazio. */
export const NO_VALUE = "—";

export type SectionVariant = "ok" | "warning" | "empty" | "closed" | "error";

export interface SectionRender {
  state: SectionState;
  variant: SectionVariant;
  tone: PillTone;
  /** O conteúdo pode ser renderizado (com ressalva em `partial`/`truncated`). */
  showsData: boolean;
  /** Os contadores têm de mostrar `—`: o número é desconhecido, não é zero. */
  countersUnknown: boolean;
  /**
   * Amostragem da **API** (`truncated {shown,total}`) — ortogonal ao `state`. Uma
   * seção pode ser `ok` com amostragem; o dado completo existe em outro endpoint.
   */
  sampled: SectionTruncation | null;
  /** Texto cru do backend (pt-BR, às vezes mensagem SAP verbatim): só tooltip. */
  reason: string;
  note: string | null;
}

const VARIANT: Record<SectionState, SectionVariant> = {
  ok: "ok",
  partial: "warning",
  // A **origem** cortou: o que veio é verdadeiro, só não é tudo.
  truncated: "warning",
  empty: "empty",
  not_collected: "closed",
  unauthorized: "error",
  error: "error",
};

const TONE: Record<SectionVariant, PillTone> = {
  ok: "ok",
  warning: "warn",
  empty: "muted",
  closed: "muted",
  error: "danger",
};

/** Estados em que o número simplesmente não é conhecido. */
const UNKNOWN_STATES: ReadonlySet<SectionState> = new Set<SectionState>([
  "not_collected",
  "error",
  "unauthorized",
]);

export function readSection(envelope: SectionEnvelope): SectionRender {
  const state = envelope.state;
  const variant = VARIANT[state];
  return {
    state,
    variant,
    tone: TONE[variant],
    showsData: variant === "ok" || variant === "warning",
    countersUnknown: UNKNOWN_STATES.has(state),
    sampled: envelope.truncated ?? null,
    reason: envelope.reason,
    note: envelope.note ?? null,
  };
}

/**
 * Texto de um contador dentro da seção.
 *
 * `empty` devolve o número: "a origem respondeu sem registros" é um zero
 * **conhecido**. Estado desconhecido ou valor ausente devolvem {@link NO_VALUE}.
 */
export function sectionCounter(
  envelope: SectionEnvelope,
  value: number | null | undefined,
  format: (n: number) => string = (n) => String(n),
): string {
  if (UNKNOWN_STATES.has(envelope.state)) return NO_VALUE;
  if (value == null || Number.isNaN(value)) return NO_VALUE;
  return format(value);
}
